// Snake_case (DB) ↔ camelCase (frontend) converters per entity.
// Keeps the rest of the codebase using the same shapes it already uses.

// ── Client ─────────────────────────────────────────────────────────────────
export const clientToRow = (c, ownerId) => ({
  id: c.id,
  owner_id: ownerId,
  name: c.name,
  email: c.email || null,
  alias_emails: Array.isArray(c.aliasEmails) ? c.aliasEmails : [],
  country: c.country || null,
  type: c.type || null,
  primary_type: c.primaryType || null,
  primary_offer_id: c.primaryOfferId || null,
  monthly_rate: Number(c.monthlyRate) || 0,
  setup_price: Number(c.setupPrice) || 0,
  start_date: c.startDate || null,
  status: c.status || "active",
  churn_date: c.churnDate || null,
  acquisition_source: c.acquisitionSource || null,
  funnel: c.funnel || null,
  notes: c.notes || null,
  external_id: c.externalId || null,
  department: c.department || null,
});

export const rowToClient = (r) => ({
  id: r.id,
  name: r.name,
  email: r.email,
  aliasEmails: Array.isArray(r.alias_emails) ? r.alias_emails : [],
  country: r.country,
  type: r.type,
  primaryType: r.primary_type,
  primaryOfferId: r.primary_offer_id,
  monthlyRate: Number(r.monthly_rate) || 0,
  setupPrice: Number(r.setup_price) || 0,
  startDate: r.start_date,
  status: r.status,
  churnDate: r.churn_date,
  acquisitionSource: r.acquisition_source,
  funnel: r.funnel,
  notes: r.notes,
  externalId: r.external_id,
  department: r.department || null,
});

// ── Offer ──────────────────────────────────────────────────────────────────
export const offerToRow = (o, ownerId) => ({
  id: o.id,
  owner_id: ownerId,
  name: o.name,
  type: o.type,
  default_amount: o.defaultAmount || 0,
  description: o.description || null,
  department: o.department || null,
});

export const rowToOffer = (r) => ({
  id: r.id,
  name: r.name,
  type: r.type,
  defaultAmount: Number(r.default_amount) || 0,
  description: r.description,
  department: r.department || null,
});

// ── Payment ────────────────────────────────────────────────────────────────
// amount             = total collected from client
// amountRevenue      = portion counted as agency revenue (rest is held budget)
// amountClientBudget = portion held to spend on client's behalf (pass-through)
export const paymentToRow = (p, ownerId) => ({
  id: p.id,
  owner_id: ownerId,
  client_id: p.clientId || null,
  offer_id: p.offerId || null,
  amount: p.amount || 0,
  amount_revenue: p.amountRevenue != null ? Number(p.amountRevenue) : null,
  amount_client_budget: Number(p.amountClientBudget) || 0,
  booked_date: p.bookedDate || null,
  due_date: p.dueDate || null,
  paid_date: p.paidDate || null,
  status: p.status,
  split_group_id: p.splitGroupId || null,
  note: p.note || null,
});

export const rowToPayment = (r) => ({
  id: r.id,
  clientId: r.client_id,
  offerId: r.offer_id,
  amount: Number(r.amount) || 0,
  // Legacy rows have amount_revenue=NULL → fall back to full amount (no split).
  amountRevenue: r.amount_revenue != null ? Number(r.amount_revenue) : Number(r.amount) || 0,
  amountClientBudget: Number(r.amount_client_budget) || 0,
  bookedDate: r.booked_date,
  dueDate: r.due_date,
  paidDate: r.paid_date,
  status: r.status,
  splitGroupId: r.split_group_id,
  note: r.note,
});

// ── Team ───────────────────────────────────────────────────────────────────
export const teamToRow = (t, ownerId) => ({
  id: t.id,
  owner_id: ownerId,
  name: t.name,
  role: t.role || null,
  email: t.email || null,
  phone: t.phone || null,
  instapay_handle: t.instapayHandle || null,
  department: t.department || null,
  type: t.type || "employee",
  is_owner: t.isOwner === true,
  monthly_salary: t.monthlySalary || 0,
  native_salary: t.nativeSalary != null ? Number(t.nativeSalary) : null,
  native_currency: t.nativeCurrency || null,
  start_date: t.startDate || null,
  end_date: t.endDate || null,
  status: t.status || "active",
  notes: t.notes || null,
  offer_allocations: t.offerAllocations || [],
  salary_by_month: t.salaryByMonth || {},
  bonus_by_month: t.bonusByMonth || {},
  payments_by_month: t.paymentsByMonth || {},
});

export const rowToTeam = (r) => ({
  id: r.id,
  name: r.name,
  role: r.role,
  email: r.email,
  phone: r.phone || null,
  instapayHandle: r.instapay_handle || null,
  department: r.department || null,
  type: r.type || "employee",
  isOwner: r.is_owner === true,
  monthlySalary: Number(r.monthly_salary) || 0,
  nativeSalary: r.native_salary != null ? Number(r.native_salary) : null,
  nativeCurrency: r.native_currency || null,
  startDate: r.start_date,
  endDate: r.end_date,
  status: r.status,
  notes: r.notes,
  offerAllocations: r.offer_allocations || [],
  salaryByMonth: r.salary_by_month || {},
  bonusByMonth: r.bonus_by_month || {},
  paymentsByMonth: r.payments_by_month || {},
});

// ── Contractor payments ────────────────────────────────────────────────────
export const contractorPaymentToRow = (p, ownerId) => ({
  id: p.id,
  owner_id: ownerId,
  contractor_id: p.contractorId,
  date: p.date,
  usd_amount: Number(p.usdAmount) || 0,
  native_amount: p.nativeAmount != null ? Number(p.nativeAmount) : null,
  native_currency: p.nativeCurrency || null,
  fx_rate: p.fxRate != null ? Number(p.fxRate) : null,
  notes: p.notes || null,
});

export const rowToContractorPayment = (r) => ({
  id: r.id,
  contractorId: r.contractor_id,
  date: r.date,
  usdAmount: Number(r.usd_amount) || 0,
  nativeAmount: r.native_amount != null ? Number(r.native_amount) : null,
  nativeCurrency: r.native_currency || null,
  fxRate: r.fx_rate != null ? Number(r.fx_rate) : null,
  notes: r.notes,
});

// ── Vacations ──────────────────────────────────────────────────────────────
export const vacationToRow = (v, ownerId) => ({
  id: v.id,
  owner_id: ownerId,
  member_id: v.memberId,
  start_date: v.startDate || null,
  end_date: v.endDate || null,
  notes: v.notes || null,
});

export const rowToVacation = (r) => ({
  id: r.id,
  memberId: r.member_id,
  startDate: r.start_date,
  endDate: r.end_date,
  notes: r.notes,
});

// ── Tool ───────────────────────────────────────────────────────────────────
export const toolToRow = (t, ownerId) => ({
  id: t.id,
  owner_id: ownerId,
  name: t.name,
  category: t.category || null,
  unit_label: t.unitLabel || null,
  status: t.status || "active",
  type: t.type || "recurring",
  notes: t.notes || null,
  units_by_month: t.unitsByMonth || {},
  price_by_month: t.priceByMonth || {},
  offer_allocations: t.offerAllocations || [],
});

export const rowToTool = (r) => ({
  id: r.id,
  name: r.name,
  category: r.category,
  unitLabel: r.unit_label,
  status: r.status,
  type: r.type || "recurring",
  notes: r.notes,
  unitsByMonth: r.units_by_month || {},
  priceByMonth: r.price_by_month || {},
  offerAllocations: r.offer_allocations || [],
});

// ── Tool transactions (variable tools) ─────────────────────────────────────
export const toolTransactionToRow = (t, ownerId) => ({
  id: t.id,
  owner_id: ownerId,
  tool_id: t.toolId,
  date: t.date,
  usd_amount: Number(t.usdAmount) || 0,
  native_amount: t.nativeAmount != null ? Number(t.nativeAmount) : null,
  native_currency: t.nativeCurrency || null,
  fx_rate: t.fxRate != null ? Number(t.fxRate) : null,
  notes: t.notes || null,
});

export const rowToToolTransaction = (r) => ({
  id: r.id,
  toolId: r.tool_id,
  date: r.date,
  usdAmount: Number(r.usd_amount) || 0,
  nativeAmount: r.native_amount != null ? Number(r.native_amount) : null,
  nativeCurrency: r.native_currency || null,
  fxRate: r.fx_rate != null ? Number(r.fx_rate) : null,
  notes: r.notes,
});

// ── One-off payment ────────────────────────────────────────────────────────
// Note: in the UI this entity is shown as "Transactions" (bank-feed actuals).
// DB table name kept as `one_off_payments` to avoid RLS/index/FK churn.
export const oneOffToRow = (p, ownerId) => ({
  id: p.id,
  owner_id: ownerId,
  name: p.name,
  category: p.category || null,
  date: p.date,
  amount: p.amount || 0,
  notes: p.notes || null,
  offer_id: p.offerId || null,
  cost_type: p.costType || "opex",
  department: p.department || null,
  status: p.status || "matched",
  matched_source_type: p.matchedSourceType || null,
  matched_source_id: p.matchedSourceId || null,
  non_expense_category: p.nonExpenseCategory || null,
  external_id: p.externalId || null,
  external_status: p.externalStatus || null,
  currency: p.currency || "USD",
  direction: p.direction || "out",
  qb_type: p.qbType || null,
});

export const rowToOneOff = (r) => ({
  id: r.id,
  name: r.name,
  category: r.category,
  date: r.date,
  amount: Number(r.amount) || 0,
  notes: r.notes,
  offerId: r.offer_id || null,
  costType: r.cost_type || "opex",
  department: r.department || null,
  status: r.status || "matched",
  matchedSourceType: r.matched_source_type || null,
  matchedSourceId: r.matched_source_id || null,
  nonExpenseCategory: r.non_expense_category || null,
  externalId: r.external_id || null,
  externalStatus: r.external_status || null,
  currency: r.currency || "USD",
  direction: r.direction || "out",
  qbType: r.qb_type || null,
});

// ── FX rate ────────────────────────────────────────────────────────────────
export const fxRateToRow = (f, ownerId) => ({
  owner_id: ownerId,
  month: f.month,
  usd_to_egp: f.usdToEgp,
});

export const rowToFxRate = (r) => ({
  month: r.month,
  usdToEgp: Number(r.usd_to_egp) || 0,
});

// ── Dividend ───────────────────────────────────────────────────────────────
// Money paid out to Ahmed / partners. Attributed to a department so per-dept
// Cash in Bank subtracts correctly.
export const dividendToRow = (d, ownerId) => ({
  id: d.id,
  owner_id: ownerId,
  date: d.date,
  department: d.department || null,
  recipient: d.recipient || null,
  amount: Number(d.amount) || 0,
  payment_method: d.paymentMethod || null,
  notes: d.notes || null,
});

export const rowToDividend = (r) => ({
  id: r.id,
  date: r.date,
  department: r.department,
  recipient: r.recipient,
  amount: Number(r.amount) || 0,
  paymentMethod: r.payment_method,
  notes: r.notes,
});

// ── Partner rule ───────────────────────────────────────────────────────────
// One row = one cut owed to a partner. Examples:
//   Waleed: basis=revenue, scope=department, scopeId="SEO", percent=50
//   Karim : basis=net_profit, scope=agency, scopeId=null, percent=25
export const partnerRuleToRow = (r, ownerId) => ({
  id: r.id,
  owner_id: ownerId,
  partner_id: r.partnerId,
  basis: r.basis,
  scope: r.scope,
  scope_id: r.scopeId || null,
  percent: Number(r.percent) || 0,
  starts_on: r.startsOn || null,
  ends_on: r.endsOn || null,
  notes: r.notes || null,
});

export const rowToPartnerRule = (r) => ({
  id: r.id,
  partnerId: r.partner_id,
  basis: r.basis,
  scope: r.scope,
  scopeId: r.scope_id || null,
  percent: Number(r.percent) || 0,
  startsOn: r.starts_on,
  endsOn: r.ends_on,
  notes: r.notes,
});

